import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { PersonComponent } from "abreact/input/markdown/nodes/PersonComponent";
import { AbImage } from "absdk/commons/ui/misc/AbImage";
import { DecoratorNode } from 'lexical';
import { $applyNodeReplacement } from 'lexical';
import React from "react";
import { getPersonExtension } from "abreact/input/markdown/utils/MarkdownUtils";
function convertPersonElement(domNode) {
  const id = domNode.getAttribute('data-lexical-id');
  const displayName = domNode.getAttribute('data-lexical-display-name');
  if (id != null) {
    const node = $createPersonNode(id, displayName || undefined);
    return {
      node
    };
  }
  return null;
}
export class PersonNode extends DecoratorNode {
  static getType() {
    return 'person';
  }
  static clone(node) {
    return new PersonNode(node.__id, node.__displayName, node.__key);
  }
  constructor(id, displayName, key) {
    super(key);
    _defineProperty(this, "__id", void 0);
    _defineProperty(this, "__displayName", void 0);
    this.__id = id;
    this.__displayName = displayName ?? id;
  }
  static importJSON(serializedNode) {
    const node = $createPersonNode(serializedNode.id, serializedNode.displayName);
    return node;
  }
  exportJSON() {
    return {
      id: this.__id,
      displayName: this.__displayName,
      type: 'person',
      version: 1
    };
  }
  updateDOM(prevNode) {
    return prevNode.__id !== prevNode.__id || prevNode.__displayName !== prevNode.__displayName;
  }
  createDOM() {
    const dom = document.createElement('span');
    dom.style.display = 'inline-block';
    return dom;
  }
  exportDOM() {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-person', 'true');
    element.setAttribute('data-lexical-id', this.__id);
    element.setAttribute('data-lexical-display-name', this.__displayName);
    return {
      element
    };
  }
  static importDOM() {
    return {
      span: domNode => {
        if (!domNode.hasAttribute('data-lexical-person')) {
          return null;
        }
        return {
          conversion: convertPersonElement,
          priority: 1
        };
      }
    };
  }
  decorate() {
    let node;
    const extension = getPersonExtension();
    if (extension) {
      node = extension.render({
        id: this.__id,
        displayName: this.__displayName
      }, this.__displayName);
      if (! /*#__PURE__*/React.isValidElement(node)) {
        throw new Error("Person extension did not return element" + node);
      }
    } else {
      // no extension, still render something.
      node = /*#__PURE__*/React.createElement("span", {
        className: "ab-markdown-person-default"
      }, /*#__PURE__*/React.createElement(AbImage, {
        image: "absdk:nav-users"
      }), this.__displayName);
    }
    return /*#__PURE__*/React.createElement(PersonComponent, {
      nodeKey: this.__key
    }, node);
  }
  getId() {
    return this.__id;
  }
  setId(id) {
    const writable = this.getWritable();
    writable.__id = id;
  }
  getDisplayName() {
    return this.__displayName;
  }
  setDisplayName(displayName) {
    const writable = this.getWritable();
    writable.__displayName = displayName;
  }
}
// Is there a person extension registered? null means no. Starts undefined.
_defineProperty(PersonNode, "extension", undefined);
export function $createPersonNode(id, displayName) {
  const personNode = new PersonNode(id, displayName);
  return $applyNodeReplacement(personNode);
}
export function $isPersonNode(node) {
  // eslint-disable-next-line abinitio/no-instanceof
  return node instanceof PersonNode;
}
//# sourceMappingURL=PersonNode.js.map
